/**
 * Mice Trail Service
 * REST calls for the Christmas Mice Trail (WordPress mu-plugin: mice-trail.php).
 *
 * Public (QR scan page):
 *   GET  /custom/v1/mice-trail/mouse/{code}
 *   POST /custom/v1/mice-trail/mouse/{code}/claim
 *   POST /custom/v1/mice-trail/mouse/{code}/photo
 *   GET  /custom/v1/mice-trail/journey
 *
 * Admin (Security Centre — uses the Data Centre login already in this browser):
 *   GET    /custom/v1/mice-trail/admin/mice
 *   POST   /custom/v1/mice-trail/admin/generate
 *   POST   /custom/v1/mice-trail/admin/mice/{id}
 *   POST   /custom/v1/mice-trail/admin/mice/{id}/reset
 *   POST   /custom/v1/mice-trail/admin/mice/{id}/ticket-email
 *   POST   /custom/v1/mice-trail/admin/ticket-emails
 *   DELETE /custom/v1/mice-trail/admin/mice/{id}
 *   POST   /custom/v1/mice-trail/admin/towns/rename
 *   GET    /custom/v1/mice-trail/admin/journey
 *   POST   /custom/v1/mice-trail/admin/journey
 */

import config from '../config/environment';

const API_BASE = `${config.apiEndpoint}/custom/v1/mice-trail`;

export const PUBLIC_SITE_URL = 'https://www.belvoircastle.com';

/** The address printed inside every QR code. */
export const mouseUrl = (code, base = PUBLIC_SITE_URL) => `${base.replace(/\/+$/, '')}/m/${code}`;

function sessionToken() {
  try { return localStorage.getItem('token') || ''; } catch { return ''; }
}

async function request(path, { method = 'GET', body, admin = false } = {}) {
  const headers = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (admin) {
    const token = sessionToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
      cache: 'no-store',
    });
  } catch {
    const err = new Error('We could not reach the server. Please check your signal and try again.');
    err.network = true;
    throw err;
  }

  const text = await response.text();
  let json = null;
  try { json = JSON.parse(text); } catch { /* handled below */ }

  if (!response.ok) {
    const err = new Error(json?.message || `Something went wrong (${response.status}).`);
    err.status = response.status;
    err.code = json?.code;
    err.data = json?.data?.data || null; // e.g. details of a mouse that was already found
    throw err;
  }
  if (!json) throw new Error('The server sent an unexpected reply.');
  return json;
}

// ---- Public ----------------------------------------------------------------

export async function getMouse(code) {
  const json = await request(`/mouse/${encodeURIComponent(code)}`);
  return json.data;
}

export async function claimMouse(code, details) {
  const json = await request(`/mouse/${encodeURIComponent(code)}/claim`, { method: 'POST', body: details });
  return json.data;
}

/** Keep the visitor's photo on Bunny when they agree it can be used on social media. */
export async function shareMousePhoto(code, { file, consent, uploadSeq = 0 }) {
  const body = new FormData();
  body.append('socialConsent', consent ? '1' : '0');
  if (uploadSeq) body.append('uploadSeq', String(uploadSeq));
  if (file) body.append('photo', file, file.name || 'mouse-photo.jpg');

  let response;
  try {
    response = await fetch(`${API_BASE}/mouse/${encodeURIComponent(code)}/photo`, {
      method: 'POST',
      body,
      cache: 'no-store',
    });
  } catch {
    const err = new Error('We could not reach the server. Your card is still saved on this phone.');
    err.network = true;
    throw err;
  }

  const text = await response.text();
  let json = null;
  try { json = JSON.parse(text); } catch { /* handled below */ }
  if (!response.ok) {
    throw new Error(json?.message || 'We could not keep a copy of that photo just now. Your card is still saved on this phone.');
  }
  return json?.data || { saved: false };
}

export async function getJourney() {
  const json = await request('/journey');
  return json.data;
}

/** Found and still-hiding totals for the town the mice are in now. Null until that town has mice. */
export function trailTally(journey) {
  const counts = journey?.counts;
  const total = Number(counts?.total) || 0;
  if (total <= 0) return null;
  const found = Math.max(0, Number(counts.found) || 0);
  return {
    town: counts.town || journey.currentTown || '',
    found,
    remaining: Math.max(0, Number(counts.remaining) || total - found),
    total,
  };
}

let tallyCache = null;
let tallyPending = null;

export function getTrailTally() {
  if (tallyCache && Date.now() - tallyCache.at < 30000) {
    return Promise.resolve(tallyCache.tally);
  }
  if (!tallyPending) {
    tallyPending = getJourney()
      .then((data) => {
        const tally = trailTally(data);
        tallyCache = { at: Date.now(), tally };
        tallyPending = null;
        return tally;
      })
      .catch((error) => {
        tallyPending = null;
        throw error;
      });
  }
  return tallyPending;
}

// ---- Admin -----------------------------------------------------------------

export async function adminListMice() {
  const json = await request('/admin/mice', { admin: true });
  return json.data || [];
}

export async function adminGenerateMice(town, count) {
  const json = await request('/admin/generate', { method: 'POST', admin: true, body: { town, count } });
  return json.data;
}

export async function adminUpdateMouse(id, fields) {
  const json = await request(`/admin/mice/${id}`, { method: 'POST', admin: true, body: fields });
  return json.data;
}

export async function adminResetMouse(id) {
  const json = await request(`/admin/mice/${id}/reset`, { method: 'POST', admin: true });
  return json.data;
}

/** Booking instructions for one winner. Skips nobody — the caller confirms a resend. */
export async function adminSendTicketEmail(id) {
  const json = await request(`/admin/mice/${id}/ticket-email`, { method: 'POST', admin: true });
  return json.data;
}

/** Booking instructions for every found winner who has not been emailed yet. */
export async function adminSendTicketEmails() {
  const json = await request('/admin/ticket-emails', { method: 'POST', admin: true });
  return json.data;
}

export async function adminDeleteMouse(id) {
  await request(`/admin/mice/${id}`, { method: 'DELETE', admin: true });
}

export async function adminRenameTown(from, to) {
  await request('/admin/towns/rename', { method: 'POST', admin: true, body: { from, to } });
}

export async function adminGetJourney() {
  const json = await request('/admin/journey', { admin: true });
  return json.data;
}

export async function adminSetJourney(fields) {
  const json = await request('/admin/journey', { method: 'POST', admin: true, body: fields });
  return json.data;
}
